import React, { useState } from "react";
import Modal from "react-bootstrap/Modal";
import { TablaDetalleNota } from "./tablaDetalleNota";

/*
 * Modal de detalle para el modulo de Auditoria.
 *
 * Recibe:
 *  - show:       muestra u oculta el modal
 *  - datos:      registro seleccionado (objeto). Si es null no se muestra nada.
 *  - titulo:     titulo del registro (ej. "Registro UPDATE en facturacompra")
 *  - subtitulo:  linea secundaria (ej. "audit_log · 24-09-2026 10:35 AM")
 *  - icono:      clase del icono del encabezado (fontawesome)
 *  - campos:     arreglo [{ label, valor }] con los datos planos del registro
 *  - cambios:    arreglo [{ campo, anterior, nuevo }] con los campos modificados
 *  - bloques:    arreglo [{ label, valor }] con los campos JSON (jsonb)
 *  - comparacion: (opcional) arreglo de columnas [{ titulo, subtitulo, icono,
 *                campos, bloques }] para mostrar lado a lado el registro de
 *                auditoria y su respaldo. Si viene, reemplaza la ficha simple.
 *  - onImprimir: si viene, se muestra el boton para imprimir el reporte
 *  - onHideCancela: cierra el modal
 */
export const VerAuditoria = (props) => {
  const {
    show,
    onHideCancela,
    onImprimir,
    datos,
    campos,
    cambios,
    bloques,
    comparacion,
    titulo,
    subtitulo,
    icono,
  } = props;
  const [copiado, setCopiado] = useState(-1);

  const formatearJson = (valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return "";
    }
    try {
      return typeof valor === "string"
        ? JSON.stringify(JSON.parse(valor), null, 2)
        : JSON.stringify(valor, null, 2);
    } catch (error) {
      return String(valor);
    }
  };

  const valorTexto = (valor) => {
    if (valor === null || valor === undefined || valor === "") {
      return "-";
    }
    return typeof valor === "object" ? JSON.stringify(valor) : String(valor);
  };

  const copiar = (valor, indice) => () => {
    try {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(formatearJson(valor));
      }
      setCopiado(indice);
      setTimeout(() => setCopiado(-1), 1500);
    } catch (error) {
      /* si el navegador no permite copiar no se hace nada */
    }
  };

  /*
   * Tarjeta de la comparacion con el respaldo. Muestra el encabezado, los
   * datos de la cabecera y, si la columna trae renglones (`detalle`), el ticket
   * de productos (codigo, nombre, cantidad, precio, IVA e importe + totales).
   */
  const tarjetaComparacion = (columna, key) => (
    <div className="aud-ticket" key={key}>
      <div className="aud-ticket-cabecera">
        <i className={`${columna.icono || "fas fa-folder-open"} me-1`}></i>
        <span>{columna.titulo}</span>
      </div>
      {columna.subtitulo ? (
        <small className="aud-ticket-subtitulo d-block">
          {columna.subtitulo}
        </small>
      ) : null}
      {(columna.campos || []).length > 0 ? (
        <div className="aud-ficha aud-ficha-nota mt-2">
          {(columna.campos || []).map((campo, i) => (
            <div className="aud-ficha-item" key={i}>
              <span className="aud-ficha-label">{campo.label}</span>
              <span className="aud-ficha-valor">{valorTexto(campo.valor)}</span>
            </div>
          ))}
        </div>
      ) : null}
      {(columna.bloques || []).map((bloque, i) =>
        formatearJson(bloque.valor) === "" ? null : (
          <div className="aud-json mt-2" key={i}>
            <div className="aud-json-cabecera">
              <span className="aud-json-titulo">{bloque.label}</span>
            </div>
            <pre>{formatearJson(bloque.valor)}</pre>
          </div>
        )
      )}
      {columna.detalle !== undefined ? (
        <div className="aud-ticket-detalle">
          <span className="aud-ticket-detalle-titulo">Detalle de la nota</span>
          <TablaDetalleNota detalle={columna.detalle} />
        </div>
      ) : null}
    </div>
  );

  const comparacionLista = comparacion || [];

  return (
    <Modal
      show={show}
      onHide={onHideCancela}
      size="lg"
      dialogClassName="modal-auditoria-detalle"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      backdrop="static"
      keyboard={false}
      scrollable
    >
      <Modal.Header className="azulGradiant header-modal">
        <div className="d-flex align-items-center">
          <i
            className={`${icono || "fas fa-search"} fa-fw me-2 fs-4 text-light`}
          ></i>
          <div className="text-light">
            <h6 className="mb-0 fw-bold">{titulo || "Detalle de Auditoría"}</h6>
            {subtitulo ? <small className="fw-normal">{subtitulo}</small> : null}
          </div>
        </div>
        <button
          className="bg-transparent"
          style={{ border: 0, margin: 0, padding: 0, color: "#ffffff" }}
          onClick={onHideCancela}
        >
          <i className="far fa-window-close"></i>
        </button>
      </Modal.Header>

      <Modal.Body
        className="bg-light"
        style={{ maxHeight: "72vh", overflowY: "auto" }}
      >
        {!datos ? (
          <p className="text-center mb-0">Sin datos.</p>
        ) : (
          <div className="container-fluid px-0">
            {comparacionLista.length > 0 ? (
              <div>
                <span className="aud-seccion-titulo d-block">
                  Comparación con el respaldo
                </span>
                <div className="aud-comparacion">
                  <div className="aud-comparacion-registro">
                    {tarjetaComparacion(comparacionLista[0], "registro")}
                  </div>
                  <div className="aud-comparacion-respaldos">
                    {comparacionLista
                      .slice(1)
                      .map((columna, index) =>
                        tarjetaComparacion(columna, "respaldo-" + index)
                      )}
                  </div>
                </div>
              </div>
            ) : (
              <React.Fragment>
                <span className="aud-seccion-titulo d-block">
                  Datos del registro
                </span>
                <div className="aud-ficha">
              {(campos || []).map((campo, index) => (
                <div className="aud-ficha-item" key={index}>
                  <span className="aud-ficha-label">{campo.label}</span>
                  <span className="aud-ficha-valor">
                    {valorTexto(campo.valor)}
                  </span>
                </div>
              ))}
            </div>

            {(cambios || []).length > 0 && (
              <div>
                <span className="aud-seccion-titulo d-block">
                  Campos modificados ({cambios.length})
                </span>
                <div className="table-responsive">
                  <table className="aud-cambios mb-0">
                    <thead>
                      <tr>
                        <th style={{ width: "30%" }}>Campo</th>
                        <th>Valor anterior</th>
                        <th>Valor nuevo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cambios.map((cambio, index) => (
                        <tr key={index}>
                          <td className="aud-campo">{cambio.campo}</td>
                          <td className="aud-anterior">
                            {valorTexto(cambio.anterior)}
                          </td>
                          <td className="aud-nuevo">
                            {valorTexto(cambio.nuevo)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {(bloques || []).map((bloque, index) =>
              formatearJson(bloque.valor) === "" ? null : (
                <div className="aud-json mt-3" key={index}>
                  <div className="aud-json-cabecera">
                    <span className="aud-json-titulo">{bloque.label}</span>
                    <button
                      type="button"
                      className="btn btn-sm btn-link py-0 px-1 text-decoration-none"
                      onClick={copiar(bloque.valor, index)}
                      title="Copiar al portapapeles"
                    >
                      {copiado === index ? (
                        <i className="fas fa-check text-success"></i>
                      ) : (
                        <i className="far fa-copy"></i>
                      )}
                    </button>
                  </div>
                  <pre>{formatearJson(bloque.valor)}</pre>
                </div>
              )
            )}
              </React.Fragment>
            )}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="py-1">
        {typeof onImprimir === "function" ? (
          <button
            className="btn btn-sm btn-outline-secondary rounded-pill px-3 me-auto"
            onClick={onImprimir}
          >
            <i className="fas fa-print me-1"></i> Imprimir reporte
          </button>
        ) : null}
        <button
          className="btn btn-sm btn-primary rounded-pill px-4"
          onClick={onHideCancela}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </Modal>
  );
};



// WEBPACK FOOTER //
// ./src/components/configuracionGeneral/modalVerAuditoria.js